import React, { useRef } from 'react';
import { User, Camera, Cake } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

function calculateAge(dobStr) {
  if (!dobStr) return null;
  try {
    const dob = new Date(dobStr);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age >= 0 ? age : null;
  } catch { return null; }
}

function formatDOB(dobStr) {
  if (!dobStr) return null;
  try {
    const d = new Date(dobStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return null; }
}

export function ParticipantHeaderV2({ participant, showNdis, canEdit, onPhotoUpdated }) {
  const fileInputRef = useRef(null);

  if (!participant) return null;

  const fullName = [participant.first_name, participant.middle_name, participant.last_name].filter(Boolean).join(' ');
  const preferredName = participant.preferred_name;
  const photoUrl = participant.profile_photo_url;
  const dob = participant.date_of_birth;
  const formattedDOB = formatDOB(dob);
  const age = calculateAge(dob);

  const handlePhotoClick = () => {
    if (canEdit && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/participants/${participant.id || participant._id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo uploaded');
      if (onPhotoUpdated) onPhotoUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="flex items-center gap-4" data-testid="participant-header-v2">
      {/* Profile Photo — clickable for edit */}
      <div
        className={`flex-shrink-0 relative group ${canEdit ? 'cursor-pointer' : ''}`}
        onClick={handlePhotoClick}
        data-testid="header-v2-photo"
      >
        {photoUrl ? (
          <img src={photoUrl} alt={fullName} className="w-16 h-16 rounded-full object-cover border-2 border-muted" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-muted/60 border-2 border-muted flex items-center justify-center">
            <User className="w-7 h-7 text-muted-foreground/50" />
          </div>
        )}
        {canEdit && (
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="photo-upload-overlay">
            <Camera className="w-5 h-5 text-white" />
          </div>
        )}
        {canEdit && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleFileSelected}
            data-testid="photo-file-input"
          />
        )}
      </div>

      {/* Name + Info */}
      <div className="flex-1 min-w-0">
        <h1 className="text-2xl font-bold tracking-tight truncate" data-testid="header-v2-fullname">
          {fullName || 'Unknown'}
        </h1>
        {preferredName && preferredName !== participant.first_name && (
          <p className="text-sm text-muted-foreground mt-0.5" data-testid="header-v2-preferred">
            Preferred: {preferredName}
          </p>
        )}
        {showNdis && participant.ndis_number && (
          <p className="text-muted-foreground font-mono text-sm mt-0.5">NDIS: {participant.ndis_number}</p>
        )}
      </div>

      {/* DOB + Age */}
      {formattedDOB && (
        <div className="flex-shrink-0 text-right" data-testid="header-v2-dob">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Cake className="w-4 h-4" />
            <span>DOB: {formattedDOB}</span>
            {age !== null && <span className="font-medium text-foreground">({age} yrs)</span>}
          </div>
        </div>
      )}
    </div>
  );
}
